import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import type { Category } from '@/types'
import { api } from '@/lib/api'
import { CATEGORIES } from '@/lib/categories'
import ItemCard from '@/components/ItemCard'
import ItemForm from '@/components/ItemForm'

interface Props {
  category: Category
}

export default function CategoryPage({ category }: Props) {
  const [showForm, setShowForm] = useState(false)
  const qc = useQueryClient()
  const config = CATEGORIES[category]

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['items', category],
    queryFn: () => api.items.list(category),
  })

  const createMutation = useMutation({
    mutationFn: api.items.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items', category] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  const subcategoryGroups = Object.entries(config.subcategories)
  const grouped: Record<string, typeof items> = {}
  for (const item of items) {
    grouped[item.subcategory] = grouped[item.subcategory] ?? []
    grouped[item.subcategory].push(item)
  }

  return (
    <div className="space-y-4">
      {/* Add button */}
      <button
        onClick={() => setShowForm(true)}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-slate-200 text-sm text-slate-500 hover:border-brand-400 hover:text-brand-600 transition-colors"
      >
        <Plus size={16} />
        Add {config.label} Record
      </button>

      {isLoading && (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-white rounded-xl border border-slate-200 animate-pulse" />)}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <p className="text-sm">No {config.label.toLowerCase()} records yet.</p>
          <p className="text-xs mt-1">Tap the button above to add one.</p>
        </div>
      )}

      {/* Group by subcategory */}
      {subcategoryGroups
        .filter(([key]) => grouped[key]?.length)
        .map(([key, sub]) => (
          <section key={key}>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{sub.label}</h3>
            <div className="space-y-2">
              {(grouped[key] ?? []).map(item => <ItemCard key={item.id} item={item} />)}
            </div>
          </section>
        ))}

      {showForm && (
        <ItemForm
          onSave={async (data) => { await createMutation.mutateAsync({ ...data, category }) }}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  )
}
